// SchemaAdminTab — one of 3 tabs in KnowledgeAdmin.jsx (added 3 Aug 2026
// for the retrieval-architecture rebuild, see ai-chat-tracker.md).
//
// Lists every table the agent's `get_table_schema` tool can retrieve.
// Each row is a table; expanding it (via the modal) shows/edits its
// columns' business meanings.
//
// "Sync from Redshift" removed from this UI on 3 Aug 2026 (see
// ai-chat-tracker.md) — the intended workflow past initial bootstrap is
// a small, deliberately curated set of derived/business tables (the raw
// information_schema sweep pulls in every raw/staging/sandbox table in
// the warehouse, which is noise the agent doesn't need and can actually
// hurt answer quality). The bigger reason it's gone, not just
// de-emphasized: `sync_from_redshift()` treats "no existing row" as
// "new table, (re)create it" with no way to distinguish "never synced"
// from "an admin deliberately deleted this" — running it again after a
// curation pass would silently resurrect every row that was just
// removed. The backend route/service still exist (unused, not deleted)
// for a future one-off re-run if a genuinely new table needs
// auto-discovering; just not exposed as a routine UI action.
//
// Access model matches the Lessons tab: amazon-internal view-only,
// admins write.

import { useState, useEffect, useMemo } from "react";
import {
  SpaceBetween, Container, Header, Box, Button, Table, Spinner, Alert,
  Modal, FormField, Input, Textarea, TextFilter,
} from "@cloudscape-design/components";
import { aiChatApi } from "./aiChatApi.js";

// "fact.orders" -> {schema: "fact", table: "orders"}. Splits on the FIRST
// dot only — a table name itself is never expected to contain a dot, but
// being defensive here costs nothing (schema names like
// "smarthub_staging_scd_t1" already contain underscores, not dots, so a
// naive single-split is safe in practice for every real name seen during
// the sync).
function splitTableName(fullName) {
  const idx = (fullName || "").indexOf(".");
  if (idx === -1) return { schema: "", table: fullName || "" };
  return { schema: fullName.slice(0, idx), table: fullName.slice(idx + 1) };
}

export default function SchemaAdminTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [deletingTable, setDeletingTable] = useState(null);
  const [editingTable, setEditingTable] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await aiChatApi.listSchema();
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || "Failed to load schema metadata.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Search matches schema, table name, business summary, AND column
  // names/meanings — a curated set of ~20-25 tables is small enough that
  // scanning every column client-side per keystroke is instant, no need
  // for a server-side search endpoint for this volume.
  const filteredItems = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      if (item.table_name?.toLowerCase().includes(q)) return true;
      if (item.business_summary?.toLowerCase().includes(q)) return true;
      return (item.columns || []).some(
        (c) => c.name?.toLowerCase().includes(q) || c.business_meaning?.toLowerCase().includes(q)
      );
    });
  }, [items, searchText]);

  const handleDelete = async (tableName) => {
    const confirmed = window.confirm(
      `Delete schema metadata for "${tableName}"? The agent will no longer be able to look up this table's business context. This cannot be undone.`
    );
    if (!confirmed) return;
    setDeletingTable(tableName);
    try {
      await aiChatApi.deleteSchema(tableName);
      setItems((prev) => prev.filter((i) => i.table_name !== tableName));
    } catch (err) {
      setError(err.message || "Failed to delete.");
    } finally {
      setDeletingTable(null);
    }
  };

  const handleSaveTable = async (updated) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.table_name === updated.table_name);
      if (idx === -1) return [...prev, updated];
      const copy = [...prev];
      copy[idx] = updated;
      return copy;
    });
    setEditingTable(null);
    setAddModalOpen(false);
  };

  return (
    <SpaceBetween size="l">
      {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}

      <Container
        header={
          <Header
            variant="h2"
            description="Column names, types, and business meaning the agent looks up via get_table_schema — instead of a static glossary pasted into every prompt."
            counter={`(${filteredItems.length}${filteredItems.length !== items.length ? ` of ${items.length}` : ""})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" onClick={load} loading={loading}>Refresh</Button>
                {isAdmin && (
                  <Button variant="primary" iconName="add-plus" onClick={() => setAddModalOpen(true)}>
                    Add table
                  </Button>
                )}
              </SpaceBetween>
            }
          >
            📊 Tables
          </Header>
        }
      >
        <SpaceBetween size="m">
          <TextFilter
            filteringText={searchText}
            onChange={({ detail }) => setSearchText(detail.filteringText)}
            filteringPlaceholder="Search by schema, table, or column name/meaning"
            filteringAriaLabel="Search tables"
          />
          {loading ? (
            <Box textAlign="center" padding="l"><Spinner size="large" /></Box>
          ) : (
            <Table
              variant="embedded"
              resizableColumns
              items={filteredItems}
              columnDefinitions={[
                {
                  id: "schema", header: "Schema", width: 140, minWidth: 100,
                  cell: (item) => <code className="ai-chat-schema-name-cell ai-chat-schema-wrap" title={splitTableName(item.table_name).schema}>{splitTableName(item.table_name).schema}</code>,
                },
                {
                  id: "table", header: "Table", width: 280, minWidth: 180,
                  cell: (item) => {
                    const { table } = splitTableName(item.table_name);
                    return (
                      <div className="ai-chat-schema-table-cell">
                        <code className="ai-chat-schema-name-cell ai-chat-schema-wrap" title={table}>{table}</code>
                        {isAdmin && (
                          <span className="ai-chat-schema-table-actions">
                            <Button variant="icon" iconName="edit" ariaLabel="Edit" onClick={() => setEditingTable(item)} />
                            <Button
                              variant="icon" iconName="remove" ariaLabel="Delete"
                              loading={deletingTable === item.table_name}
                              onClick={() => handleDelete(item.table_name)}
                            />
                          </span>
                        )}
                      </div>
                    );
                  },
                },
                {
                  id: "business_summary", header: "Business summary", minWidth: 250,
                  cell: (item) => item.business_summary
                    ? <span className="ai-chat-content-cell">{item.business_summary}</span>
                    : <Box color="text-body-secondary">(none yet)</Box>,
                },
                {
                  id: "primary_key", header: "Primary key", width: 200, minWidth: 120,
                  cell: (item) => (item.primary_key_columns || []).length
                    ? <span className="ai-chat-chip-list">{(item.primary_key_columns || []).map((k, i) => <span key={i} className="ai-chat-chip">{k}</span>)}</span>
                    : <Box color="text-body-secondary">-</Box>,
                },
                {
                  id: "columns", header: "Columns", width: 120, minWidth: 40,
                  cell: (item) => (item.columns || []).length,
                },
              ]}
              empty={
                <Box textAlign="center" color="inherit" padding="l">
                  <b>{searchText ? "No matching tables" : "No tables yet"}</b>
                  {!searchText && (
                    <Box color="text-body-secondary">Click "Add table" to add one manually.</Box>
                  )}
                </Box>
              }
            />
          )}
        </SpaceBetween>
      </Container>

      {editingTable && (
        <TableEditModal table={editingTable} onDismiss={() => setEditingTable(null)} onSaved={handleSaveTable} />
      )}
      {addModalOpen && (
        <TableEditModal table={null} onDismiss={() => setAddModalOpen(false)} onSaved={handleSaveTable} />
      )}
    </SpaceBetween>
  );
}

// Handles both "add a brand-new table" (table=null) and "edit an
// existing table's summary + per-column business meaning" in one modal —
// the create/edit API shapes are similar enough (both ultimately call
// createSchema, which upserts) that splitting into two components would
// just duplicate the column-editing UI.
function TableEditModal({ table, onDismiss, onSaved }) {
  const isNew = !table;
  const [tableName, setTableName] = useState(table?.table_name || "");
  const [summary, setSummary] = useState(table?.business_summary || "");
  const [pkText, setPkText] = useState((table?.primary_key_columns || []).join(", "));
  const [columns, setColumns] = useState(table?.columns || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const updateColumn = (idx, field, value) => {
    setColumns((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const addColumn = () => {
    setColumns((prev) => [...prev, { name: "", type: "", business_meaning: "" }]);
  };

  const removeColumn = (idx) => {
    setColumns((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!tableName.trim()) {
      setError("Table name is required (e.g. fact.orders).");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const primary_key_columns = pkText.split(",").map((s) => s.trim()).filter(Boolean);
      const saved = await aiChatApi.createSchema({
        table_name: tableName.trim(),
        business_summary: summary,
        primary_key_columns,
        columns: columns.filter((c) => c.name.trim()),
      });
      onSaved(saved);
    } catch (err) {
      setError(err.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      size="large"
      header={isNew ? "Add table" : `Edit ${table.table_name}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleSave}>Save</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && <Alert type="error">{error}</Alert>}
        <FormField label="Table name" description="Fully-qualified, e.g. fact.orders">
          <Input value={tableName} onChange={({ detail }) => setTableName(detail.value)} disabled={!isNew} />
        </FormField>
        <FormField label="Business summary" description="One or two sentences on what this table is for.">
          <Textarea value={summary} onChange={({ detail }) => setSummary(detail.value)} rows={2} />
        </FormField>
        <FormField label="Primary key" description="Comma-separated column name(s) that uniquely identify a row, e.g. orderid or shopid, order_date. Also tells you the table's grain — e.g. shopid, order_date means one row per shop per day.">
          <Input value={pkText} onChange={({ detail }) => setPkText(detail.value)} placeholder="e.g. orderid" />
        </FormField>

        <Box variant="h4">Columns</Box>
        {columns.map((col, idx) => (
          <SpaceBetween key={idx} direction="horizontal" size="xs">
            <Input placeholder="column_name" value={col.name} onChange={({ detail }) => updateColumn(idx, "name", detail.value)} />
            <Input placeholder="type" value={col.type} onChange={({ detail }) => updateColumn(idx, "type", detail.value)} />
            <Input
              placeholder="Business meaning"
              value={col.business_meaning}
              onChange={({ detail }) => updateColumn(idx, "business_meaning", detail.value)}
            />
            <Button variant="icon" iconName="remove" ariaLabel="Remove column" onClick={() => removeColumn(idx)} />
          </SpaceBetween>
        ))}
        <Button iconName="add-plus" onClick={addColumn}>Add column</Button>
      </SpaceBetween>
    </Modal>
  );
}
